import { useEffect, useRef } from "react";
import { Clock, Bike } from "lucide-react";
import { BRAND_COLOR } from "../webOrderApi";

// The Order page header's Clock-icon dropdown — Shop Timing (Settings →
// Business Profile → Business Hours) and Home Delivery Time (→ Delivery
// Hours), both already condensed into a short, ready-to-render group list
// server-side (see business-hours.util.ts's summarizeBusinessHoursWeek) —
// this component only ever renders what it's given, no day-grouping logic
// lives here. A plain anchored dropdown (not a full-screen modal) — closes
// on an outside click/tap or Escape, same as any standard tooltip/popover.
export default function TimingPopover({ isOpen, onClose, businessHoursSummary, deliveryHoursSummary }) {
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    function handlePointerDown(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) onClose();
    }
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={popoverRef}
      // w-[min(...)] + the max-w clamp together guarantee this never causes
      // horizontal overflow, even on the narrowest real phones (~320px
      // logical width) — a plain fixed w-72/w-80 could still overflow past
      // the left edge of the screen depending on exactly how much room
      // sits between the trigger and the header's left content.
      className="absolute right-0 top-full z-50 mt-2 w-[calc(100vw-2rem)] max-w-72 sm:max-w-80 rounded-2xl border border-slate-200 bg-white shadow-xl animate-in fade-in zoom-in-95 slide-in-from-top-1 fill-mode-forwards duration-150 origin-top-right"
    >
      <div className="max-h-[60vh] overflow-y-auto px-4 py-4 space-y-5">
        {businessHoursSummary?.length > 0 && (
          <TimingSection icon={Clock} title="Shop Timing" groups={businessHoursSummary} />
        )}
        {deliveryHoursSummary?.length > 0 && (
          <TimingSection icon={Bike} title="Home Delivery Time" groups={deliveryHoursSummary} />
        )}
      </div>
    </div>
  );
}

function TimingSection({ icon: Icon, title, groups }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 shrink-0" style={{ color: BRAND_COLOR }} />
        <h3 className="text-sm font-extrabold text-slate-900">{title}</h3>
      </div>
      <div className="space-y-2 pl-6">
        {groups.map((group, i) => (
          <div key={i}>
            <p className="text-xs font-bold text-slate-700">{group.label}</p>
            {group.lines.map((line, j) => (
              <p key={j} className="text-xs text-slate-500">{line}</p>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
